import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { addDays, format, parseISO } from 'date-fns';
import MealPlanMainContent from '@/components/meal-planning/manual/MealPlanMainContent';
import DietReportModal from '@/components/meal-planning/manual/DietReportModal';
import MealSelectionValidationModal from '@/components/meal-planning/MealSelectionValidationModal';
import { Button } from '@/components/ui/button';
import { useMealPlanState } from '@/hooks/useMealPlanState';
import { useClientPreferences } from '@/hooks/useClientPreferences';
import { DeleteMealSlotOptions, MealType } from '@/types/meal-plan';
import { Client } from '@/types/client';
import { getClientById, getManualMealPlanDraft, getDraftById, getMealPlanForDisplay, finalizeManualMealPlan, validateManualMealPlanFinalization, createMealSlot, deleteMealSlot, getDrvPrimaryTypes, changeMealPlanDates, DrvPrimaryType, updateMealPlanDayName, type ManualFinalizeValidation } from '@/lib/apiService';
import { normalizeManualDraftApiResponse, isManualDraftApiShape } from '@/utils/meal-plan-draft-normalizer';
import { getNormalizedCreationMethod } from '@/utils/feedback';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { trackMealPlanFinalizeClicked } from '@/lib/analytics/trackers/mealPlanActions';

import { AlertTriangle, CheckCircle, Loader2, Calendar, ShieldCheck } from 'lucide-react';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import { FeedbackContext } from '@/components/feedback/FeedbackPanel';
import CreateMealPlanHeader from '@/components/meal-planning/CreateMealPlanHeader';
import { ChangeMealPlanDatesModal } from '@/components/meal-planning/ChangeMealPlanDatesModal';
import ManualPlanConfirmDialog from '@/components/meal-planning/manual/ManualPlanConfirmDialog';
import ClinicalWarningsSummary from '@/components/meal-planning/manual/ClinicalWarningsSummary';
import { MealSuggestionsClientSummaryStrip } from '@/components/meal-planning/manual/MealSuggestionsClientSummaryStrip';
import { countClinicalIssueGroups } from '@/utils/clinical-finalize-warnings';

// Meal type configuration interface
export interface MealTypeConfig {
  mealType: string;
  mealName: string;
  targetCalories?: number; // Made optional for manual meal plans
  timeSlot?: string;
  uniqueId?: string;
}

type DateConflictItem = {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
};

function extractDateConflicts(error: unknown): DateConflictItem[] {
  const responseData = (error as { response?: { data?: unknown } } | null)?.response?.data;
  const payload = responseData && typeof responseData === 'object' ? responseData as Record<string, unknown> : {};
  if (payload.code !== 'MEAL_PLAN_DATE_CONFLICT' || !Array.isArray(payload.conflicts)) return [];
  return payload.conflicts
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const rec = item as Record<string, unknown>;
      return {
        id: String(rec.id ?? ''),
        name: String(rec.name ?? 'Meal plan'),
        startDate: typeof rec.startDate === 'string' ? rec.startDate : null,
        endDate: typeof rec.endDate === 'string' ? rec.endDate : null,
      };
    })
    .filter((item): item is DateConflictItem => Boolean(item?.id));
}

function formatConflictDateRange(item: DateConflictItem): string {
  const formatDate = (value: string | null) => {
    if (!value) return '';
    const date = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const start = formatDate(item.startDate);
  const end = formatDate(item.endDate);
  return start && end && start !== end ? `${start} - ${end}` : start || end;
}

const ManualMealPlanDraftPage = () => {
  const { draftId, clientId: clientIdParam } = useParams<{ draftId: string; clientId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { from?: string; planName?: string } | null;
  const fallbackBackPath = '/manual-meal-plan';
  const handleBackClick = () => {
    if (locationState?.from) {
      navigate(locationState.from);
    } else {
      navigate(fallbackBackPath);
    }
  };
  
  // Get clientId from URL params (path takes priority, query supported for backward compatibility)
  const clientIdFromUrl = clientIdParam || searchParams.get('clientId') || '';
  const finalizedFallbackBackPath = clientIdFromUrl ? `/clients/${clientIdFromUrl}/details-v2/meal-plan` : '/meal-planning';
  const dayFromUrl = searchParams.get('day') ? parseInt(searchParams.get('day')!, 10) : null;

  // Client state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loadingClientProfile, setLoadingClientProfile] = useState(false);
  const [loadingDraftData, setLoadingDraftData] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [changeDatesModalOpen, setChangeDatesModalOpen] = useState(false);
  const [isFinalizeConfirmOpen, setIsFinalizeConfirmOpen] = useState(false);
  const [finalizeDateConflicts, setFinalizeDateConflicts] = useState<DateConflictItem[]>([]);
  const [finalizeValidation, setFinalizeValidation] = useState<ManualFinalizeValidation | null>(null);
  const [isCheckingHardBlocks, setIsCheckingHardBlocks] = useState(false);
  const [isDeletingAnyMealSlot, setIsDeletingAnyMealSlot] = useState(false);
  const [drvPrimaryTypes, setDrvPrimaryTypes] = useState<DrvPrimaryType[]>([]);
  const [dayNames, setDayNames] = useState<Record<number, string | null>>({});
  
  // Combined loading state - show loader until both APIs are successful

  const isLoading = loadingClientProfile || loadingDraftData;

  const {
    planDuration,
    days,
    activeDay,
    setActiveDay,
    isReportModalOpen,
    setIsReportModalOpen,
    meals,
    mealInfo,
    updateMealInfo,
    handleDurationChange,
    calculateDailyNutrients,
    handleAddFood,
    handleRemoveFood,
    handleSwapFood,
    handleCopyDays,
    loadFromDraft,
  } = useMealPlanState({ mealTypes: [], draftId });

  const manualDays = useMemo(() => {
    const daysFromSuggestions = Array.isArray(draftData?.suggestions)
      ? draftData.suggestions
          .map((suggestion: any) => Number(suggestion?.day))
          .filter((day: number) => Number.isInteger(day) && day > 0)
          .sort((a: number, b: number) => a - b)
      : [];

    if (daysFromSuggestions.length > 0) {
      return daysFromSuggestions;
    }

    const duration = Number(draftData?.durationDays ?? draftData?.days);
    if (Number.isInteger(duration) && duration > 0) {
      return Array.from({ length: duration }, (_, index) => index + 1);
    }

    return days;
  }, [draftData, days]);

  const effectivePlanDuration = manualDays.length > 0 ? manualDays.length : planDuration;

  // Override getMealSlots to use day-specific meal types from suggestions
  const getMealSlots = (day: number) => {
    if (!draftData?.suggestions) return [];
    
    const daySuggestion = draftData.suggestions.find((s: any) => s.day === day);
    if (!daySuggestion?.meals) return [];
    
    const dayMeals = meals[day] || {};
    const dayMealInfo = mealInfo[day] || {};
    
    return Object.entries(daySuggestion.meals).map(([mealKey, mealData]: [string, any], index) => {
      const items = dayMeals[mealKey] || [];
      const fromState = dayMealInfo[mealKey];
      const apiName = String((mealData as any)?.mealName ?? '').trim();
      const apiTime = String((mealData as any)?.mealTime ?? (mealData as any)?.timeSlot ?? '').trim();
      const apiKcal =
        typeof (mealData as any)?.targetCalories === 'number' && Number.isFinite((mealData as any).targetCalories)
          ? (mealData as any).targetCalories
          : undefined;

      return {
        id: `${day}-${mealKey}-${index}`,
        type: mealKey as MealType,
        mealType: mealKey,
        stateKey: mealKey,
        name: String(fromState?.name ?? '').trim() || apiName,
        timeSlot: String(fromState?.timeSlot ?? '').trim() || apiTime,
        targetKcal: fromState?.targetKcal != null ? fromState.targetKcal : apiKcal,
        targetCalories: fromState?.targetKcal != null ? fromState.targetKcal : apiKcal,
        foodItems: items,
        items
      };
    });
  };

  // Get all unique meal types for the report modal
  const getAllMealTypes = (): MealTypeConfig[] => {
    if (!draftData?.suggestions) return [];
    
    const allMealTypesMap = new Map<string, MealTypeConfig>();
    
    draftData.suggestions.forEach((daySuggestion: any) => {
      if (daySuggestion?.meals) {
        Object.entries(daySuggestion.meals).forEach(([mealKey, mealData]: [string, any], index) => {
          if (!allMealTypesMap.has(mealKey)) {
            const apiMealName = String((mealData as any)?.mealName ?? '').trim();
            const apiMealType = String((mealData as any)?.mealType ?? '').trim();
            allMealTypesMap.set(mealKey, {
              mealType: apiMealType || mealKey,
              mealName: apiMealName,
              targetCalories:
                typeof (mealData as any)?.targetCalories === 'number' && Number.isFinite((mealData as any).targetCalories)
                  ? (mealData as any).targetCalories
                  : undefined,
              timeSlot: String((mealData as any)?.mealTime ?? (mealData as any)?.timeSlot ?? '').trim(),
              uniqueId: `${mealKey}-${index}`
            });
          }
        });
      }
    });
    
    return Array.from(allMealTypesMap.values());
  };

  const { clientPreferences } = useClientPreferences();
  
  // Day-wise preferences (overrides client defaults)
  // Note: Allergies are ALWAYS from client defaults and not customizable per day
  const [dayWisePreferences, setDayWisePreferences] = useState<{
    [day: number]: {
      dietaryPreferences?: string[];
      cuisineTypes?: string[];
      useClientDefaults?: boolean;
    };
  }>({});

  // Edit mode for day preferences
  const [editingDay, setEditingDay] = useState<number | null>(null);

  // Client defaults from GET /clients/{id}: top-level allergies, dietaryPreferences, cuisineTypesPreferred (fallback to legacy clientGoal if API returns it)
  const legacyGoal = selectedClient && (selectedClient as { clientGoal?: { allergies?: string[]; preferences?: string[]; cuisineTypes?: string[] } }).clientGoal;
  const clientAllergies: string[] = selectedClient?.allergies ?? legacyGoal?.allergies ?? [];
  const clientDietaryPreferences: string[] = selectedClient?.dietaryPreferences ?? legacyGoal?.preferences ?? [];
  const clientCuisineTypes: string[] = selectedClient?.cuisineTypesPreferred ?? legacyGoal?.cuisineTypes ?? [];

  // Get preferences for a specific day (with fallback to client defaults)
  const getPreferencesForDay = (day: number) => {
    const dayPrefs = dayWisePreferences[day];
    
    // Allergies ALWAYS come from client (never customized per day)
    const allergies = clientAllergies;
    
    if (dayPrefs && !dayPrefs.useClientDefaults) {
      return {
        allergies,
        dietaryPreferences: dayPrefs.dietaryPreferences ?? clientDietaryPreferences,
        cuisineTypes: dayPrefs.cuisineTypes ?? clientCuisineTypes
      };
    }
    
    return {
      allergies,
      dietaryPreferences: clientDietaryPreferences,
      cuisineTypes: clientCuisineTypes
    };
  };

  // Check if a day has custom preferences
  const hasCustomPreferences = (day: number) => {
    const dayPrefs = dayWisePreferences[day];
    return dayPrefs && !dayPrefs.useClientDefaults;
  };

  // Set day-wise preferences (only dietaryPreferences and cuisineTypes, NOT allergies)
  const handleSetDayPreferences = (day: number, field: 'dietaryPreferences' | 'cuisineTypes', value: string[]) => {
    setDayWisePreferences(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
        useClientDefaults: false
      }
    }));
  };

  // Reset to client defaults
  const handleResetToDefaults = (day: number) => {
    setDayWisePreferences(prev => {
      const newPrefs = { ...prev };
      delete newPrefs[day];
      return newPrefs;
    });
  };

  // Load client profile and meal programs on mount
  useEffect(() => {
    const loadClientProfile = async () => {
      if (!draftId || !clientIdFromUrl) {
        showErrorToast("Draft ID or Client ID not found. Redirecting...", "Missing Information");
        navigate('/manual-meal-plan');
        return;
      }

      const resolveDraftRouteRedirect = (data: any): string | null => {
        if (!data) return null;

        const status = String(
          data?.status ??
          data?.planStatus ??
          data?.plan_status ??
          data?.state ??
          ''
        ).toLowerCase();

        if (['finalized', 'completed', 'published'].includes(status)) {
          return `/finalized-meal-plan/${draftId}/client/${clientIdFromUrl}`;
        }

        const method = getNormalizedCreationMethod(data);
        if (method === 'ai_generated') {
          return `/ai-meal-plan/draft/${draftId}/client/${clientIdFromUrl}`;
        }
        if (method === 'auto_generated') {
          return `/automated-meal-plan/draft/${draftId}/client/${clientIdFromUrl}`;
        }

        return null;
      };

      setLoadingClientProfile(true);
      setLoadingDraftData(true);
      try {
        // Fetch client profile and manual-draft details in parallel
        const [clientResponse, draftResponseRaw] = await Promise.all([
          getClientById(clientIdFromUrl),
          getManualMealPlanDraft(draftId!).catch(() => null),
        ]);

        const client = clientResponse.client as Client;
        setSelectedClient(client);
        
        // Fetch DRV primary types based on client's guideline
        // Priority: drvRequirements.source -> drvSource -> default
        const source = client.drvRequirements?.source || client.drvSource || 'iom_usa';
        try {
          // Fire and forget or handle error gracefully - shouldn't block main flow too much
          // But maybe we want it before render?
          // Let's do it here
          const drvResponse = await getDrvPrimaryTypes({ source, limit: 200 });
          if (drvResponse.items) {
            setDrvPrimaryTypes(drvResponse.items);
          }
        } catch (error) {
          console.error('Failed to fetch DRV types:', error);
          // Non-critical, fallback to defaults
        }
        
        const manualDraftData = (draftResponseRaw as { data?: any } | null)?.data;
        if (manualDraftData) {
          const redirectPath = resolveDraftRouteRedirect(manualDraftData);
          if (redirectPath) {
            navigate(redirectPath, { replace: true });
            return;
          }

          const raw = manualDraftData;
          const normalized = isManualDraftApiShape(raw) ? normalizeManualDraftApiResponse(raw) : raw;
          setDraftData(normalized);
          
          const duration = normalized.durationDays ?? normalized.days;
          if (duration) {
            handleDurationChange(duration);
          }
          return;
        }

        // Handle stale links: this manual route may be opened for AI/auto drafts or finalized plans.
        const genericDraftResponse = await getDraftById(draftId!).catch(() => null);
        const genericRedirectPath = resolveDraftRouteRedirect(
          (genericDraftResponse as { data?: any } | null)?.data
        );
        if (genericRedirectPath) {
          navigate(genericRedirectPath, { replace: true });
          return;
        }

        const finalizedResponse = await getMealPlanForDisplay(draftId!).catch(() => null);
        const finalizedRedirectPath = resolveDraftRouteRedirect(
          (finalizedResponse as { data?: any } | null)?.data
        );
        if (finalizedRedirectPath) {
          navigate(finalizedRedirectPath, { replace: true });
          return;
        }

        showErrorToast('Meal plan not found.', 'Error Loading Draft');
      } catch (error) {
        showErrorToast(error, "Error Loading Profile");
      } finally {
        setLoadingClientProfile(false);
        setLoadingDraftData(false);
      }
    };

    loadClientProfile();
  }, [draftId, clientIdFromUrl, navigate]);

  // Load existing recipes from draft when data is available
  useEffect(() => {
    if (draftData) {
      loadFromDraft(draftData);

      // Initialize day names from API so custom labels (e.g. "detox") show after reload
      const suggestions = draftData.suggestions;
      if (Array.isArray(suggestions)) {
        setDayNames((prev) => {
          const next = { ...prev };
          suggestions.forEach((s: { day?: number; dayNumber?: number; dayName?: string | null }) => {
            const dayNum = s.day ?? s.dayNumber;
            if (dayNum != null && s.dayName != null && String(s.dayName).trim() !== '') {
              next[dayNum] = String(s.dayName).trim();
            }
          });
          return next;
        });
      }

      // Set active day from URL params if provided
      if (dayFromUrl && manualDays.includes(dayFromUrl)) {
        setActiveDay(dayFromUrl);
      }
    }
  }, [draftData, loadFromDraft, dayFromUrl, manualDays, setActiveDay]);

  useEffect(() => {
    if (manualDays.length === 0) return;
    if (!manualDays.includes(activeDay)) {
      setActiveDay(manualDays[0]);
    }
  }, [manualDays, activeDay, setActiveDay]);

  const getNutrientsByDay = () => {
    const nutrientsByDay: { [day: number]: any } = {};
    manualDays.forEach(day => {
      nutrientsByDay[day] = calculateDailyNutrients(day);
    });
    return nutrientsByDay;
  };

  // Check if all meal slots have at least one recipe
  const validateMealPlan = () => {
    const emptySlots: { day: number; mealType: string }[] = [];
    
    manualDays.forEach(day => {
      const dayMealSlots = getMealSlots(day);
      dayMealSlots.forEach(slot => {
        if (slot.items.length === 0) {
          emptySlots.push({
            day,
            mealType: slot.name // Using name as the display label
          });
        }
      });
    });
    
    return emptySlots;
  };

  const manualSlotStateKey = JSON.stringify({ suggestions: draftData?.suggestions, meals });
  const allMealSlotsFilled = manualDays.length > 0 && validateMealPlan().length === 0;

  useEffect(() => {
    if (!draftId || draftData?.status !== 'draft') {
      setFinalizeValidation(null);
      setIsCheckingHardBlocks(false);
      return;
    }

    let cancelled = false;
    setIsCheckingHardBlocks(true);
    const timer = window.setTimeout(() => {
      validateManualMealPlanFinalization(draftId)
        .then((response) => {
          if (!cancelled) setFinalizeValidation(response.data);
        })
        .catch(() => {
          if (!cancelled) setFinalizeValidation(null);
        })
        .finally(() => {
          if (!cancelled) setIsCheckingHardBlocks(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [draftId, draftData?.status, manualSlotStateKey]);

  // Handle finalize meal plan with validation
  const handleFinalizeMealPlan = async () => {
    if (!draftId || !selectedClient) return;

    // Validate meal plan first
    const missingSelections = validateMealPlan();
    
    if (missingSelections.length > 0) {
      // Show validation modal instead of blocking
      setValidationModalOpen(true);
      return;
    }

    setIsCheckingHardBlocks(true);
    try {
      const response = await validateManualMealPlanFinalization(draftId);
      setFinalizeValidation(response.data);
      if (!response.data.canFinalize) {
        showErrorToast('Resolve the clinical hard blocks shown below before finalizing.', 'Finalization blocked');
        return;
      }
    } catch (error) {
      showErrorToast(error, 'Could not run final safety checks');
      return;
    } finally {
      setIsCheckingHardBlocks(false);
    }

    setIsFinalizeConfirmOpen(true);
  };

  // Proceed with finalizing after validation
  const proceedWithFinalize = async (overwriteConflicts = false) => {
    if (!draftId || !selectedClient) return;

    const userChosenName = (location.state as { planName?: string } | null)?.planName?.trim();
    const timestamp = format(new Date(), 'MMM d, yyyy HH:mm');
    const basePlanName = `${selectedClient.firstName} ${selectedClient.lastName} - Manual Plan`;
    const planName = userChosenName || `${basePlanName} (${timestamp})`;

    trackMealPlanFinalizeClicked({
      product_area: 'meal_plans',
      meal_plan_surface: 'manual_draft',
    });

    setIsFinalizeConfirmOpen(false);
    setIsFinalizing(true);
    try {
      const response = await finalizeManualMealPlan(draftId, planName, undefined, overwriteConflicts);
      const finalizedResponse = response as { success: boolean; data?: { mealPlanId?: string; id?: string } };

      if (finalizedResponse.success) {
        const planId = finalizedResponse.data?.mealPlanId ?? finalizedResponse.data?.id ?? draftId;
        setTimeout(() => {
          navigate(
            `/finalized-meal-plan/${planId}/client/${clientIdFromUrl}`,
            {
              state: {
                from: locationState?.from || finalizedFallbackBackPath,
              },
            }
          );
        }, 1000);
      } else {
        throw new Error('Failed to finalize meal plan');
      }
    } catch (error) {
      const conflicts = extractDateConflicts(error);
      if (conflicts.length > 0) {
        setFinalizeDateConflicts(conflicts);
        setIsFinalizeConfirmOpen(true);
        return;
      }
      const validation = (error as { response?: { data?: { details?: { validation?: ManualFinalizeValidation } } } })?.response?.data?.details?.validation;
      if (validation) setFinalizeValidation(validation);
      showErrorToast(error, "Error Finalizing Meal Plan");
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleValidationCancel = () => setValidationModalOpen(false);

  const handleAddMealType = async (day: number, mealName: string, mealTime: string, targetCalories?: number | null) => {
    if (!draftId) return;

    try {
      const response = await createMealSlot(draftId, day, mealName, mealTime, targetCalories);
      const slotResponse = response as { success: boolean; data?: any };

      if (slotResponse.success && slotResponse.data) {
        handleDraftDataUpdate(slotResponse.data);
      } else if (!slotResponse.success) {
        throw new Error('Failed to add meal type');
      }
    } catch (error) {
      showErrorToast(error, "Error Adding Meal Type");
    }
  };

  // Handle deleting meal slot
  const handleDeleteMealSlot = async (day: number, mealName: string, options?: DeleteMealSlotOptions) => {
    if (!draftId) return;

    // Prevent multiple meal slot deletions
    if (isDeletingAnyMealSlot) {
      throw new Error('Please wait for current meal slot deletion to complete');
    }

    setIsDeletingAnyMealSlot(true);
    try {
      const response = await deleteMealSlot(draftId, day, mealName, options);
      const deleteResponse = response as { success: boolean; data?: any };

      if (deleteResponse.success && deleteResponse.data) {
        // Use response data directly - this will update both draftData and mealPrograms
        handleDraftDataUpdate(deleteResponse.data);
      } else {
        throw new Error('Failed to delete meal slot');
      }
    } catch (error) {
      throw error; // Re-throw so MealSlotCard can show error toast
    } finally {
      setIsDeletingAnyMealSlot(false);
    }
  };

  const handleDraftDataUpdate = (updatedDraftData: any) => {
    const normalized = isManualDraftApiShape(updatedDraftData)
      ? normalizeManualDraftApiResponse(updatedDraftData)
      : updatedDraftData;
    setDraftData(normalized);
  };

  const refreshManualDraft = async () => {
    if (!draftId) return null;
    const res = await getManualMealPlanDraft(draftId);
    const raw = (res as { data?: any })?.data;
    if (!raw) return null;
    const normalized = isManualDraftApiShape(raw) ? normalizeManualDraftApiResponse(raw) : raw;
    setDraftData(normalized);
    loadFromDraft(normalized);
    return normalized;
  };

  // Derive current date range for change-dates modal (manual plans only)
  const changeDatesParams = (() => {
    if (!draftData || !draftId || draftData.status !== 'draft') return null;
    const start = draftData.startDate ?? draftData.suggestions?.[0]?.date;
    const dur = draftData.durationDays ?? draftData.days ?? draftData.suggestions?.length ?? planDuration;
    if (!start || !dur) return null;
    let end = draftData.endDate;
    if (!end && start && dur >= 1) {
      try {
        end = format(addDays(parseISO(start), dur - 1), 'yyyy-MM-dd');
      } catch {
        end = start;
      }
    }
    return {
      mealPlanId: draftId,
      currentStartDate: start,
      currentEndDate: end ?? start,
      currentDurationDays: dur,
    };
  })();

  const handleAddDateForCopy = async (_fromDay: number) => {
    if (!draftId) return null;
    const startDate = draftData?.startDate ?? draftData?.suggestions?.[0]?.date;
    const currentDuration = draftData?.durationDays ?? draftData?.days ?? draftData?.suggestions?.length ?? effectivePlanDuration;
    if (!startDate || !currentDuration) {
      showErrorToast('Could not determine the current plan dates. Please refresh and try again.', 'Unable to add date');
      return null;
    }
    if (Math.max(Number(currentDuration) || 0, manualDays.length) >= 7) {
      showErrorToast('Manual meal plans can include up to 7 days. Remove a day before adding another one.', 'Maximum reached');
      return null;
    }

    let endDate = draftData?.endDate;
    if (!endDate) {
      try {
        endDate = format(addDays(parseISO(startDate), currentDuration - 1), 'yyyy-MM-dd');
      } catch {
        endDate = startDate;
      }
    }

    try {
      const nextEndDate = format(addDays(parseISO(endDate), 1), 'yyyy-MM-dd');
      const response = await changeMealPlanDates(draftId, {
        newStartDate: startDate,
        newEndDate: nextEndDate,
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to add new date');
      }

      const updatedDraft = await refreshManualDraft();
      if (!updatedDraft?.suggestions || !Array.isArray(updatedDraft.suggestions)) return null;
      const newestDay = updatedDraft.suggestions
        .map((suggestion: any) => Number(suggestion?.day))
        .filter((day: number) => Number.isInteger(day) && day > 0)
        .sort((a: number, b: number) => a - b)
        .at(-1);

      return newestDay ?? null;
    } catch (err) {
      showErrorToast(err, 'Failed to add new date');
      return null;
    }
  };

  const handleChangeDatesSuccess = async () => {
    if (!draftId) return;
    setLoadingDraftData(true);
    try {
      await refreshManualDraft();
    } catch (err) {
      showErrorToast(err, 'Failed to refresh plan');
    } finally {
      setLoadingDraftData(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Meal Plan</h3>
          <p className="text-gray-600">
            {loadingClientProfile && loadingDraftData 
              ? "Loading client profile and draft data..." 
              : loadingClientProfile 
                ? "Loading client profile..." 
                : "Loading draft data..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="space-y-6 p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7">
      <CreateMealPlanHeader
        title="Manual Meal Plan"
        backLabel="Back"
        inlineBackButton
        onBack={handleBackClick}
        actions={
          <div className="flex items-center gap-2">
            {changeDatesParams && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChangeDatesModalOpen(true)}
                className="gap-1.5"
              >
                <Calendar className="h-4 w-4" />
                Change dates
              </Button>
            )}
            <FeedbackButton
            clientId={clientIdFromUrl || undefined}
            label="Testing Feedback"
            defaultContext={FeedbackContext.MANUAL_MEAL_PLAN_GENERATION}
            title="Testing Feedback – Manual Meal Planning"
            description="Please provide feedback on the Manual Meal Planning process, including recipe selection, meal configuration, and overall workflow. Please specify the date of feedback. Multiple feedback entries allowed."
            titlePlaceholder="e.g., Manual meal planning workflow issues"
            detailsPlaceholder="Describe the steps, date of feedback, and observations when creating and configuring manual meal plans."
            submitLabel="Save Feedback"
            successDescription="Thanks for helping us improve Manual Meal Planning."
          />
          </div>
        }
      />

      {changeDatesParams && (
        <ChangeMealPlanDatesModal
          open={changeDatesModalOpen}
          onOpenChange={setChangeDatesModalOpen}
          mealPlanId={changeDatesParams.mealPlanId}
          currentStartDate={changeDatesParams.currentStartDate}
          currentEndDate={changeDatesParams.currentEndDate}
          currentDurationDays={changeDatesParams.currentDurationDays}
          onSuccess={handleChangeDatesSuccess}
        />
      )}

      {selectedClient && (
        <MealSuggestionsClientSummaryStrip client={selectedClient} clientId={clientIdFromUrl} />
      )}

      <MealPlanMainContent
        days={manualDays}
        activeDay={activeDay}
        onActiveDayChange={setActiveDay}
        getMealSlots={getMealSlots}
        onAddFood={handleAddFood}
        onRemoveFood={handleRemoveFood}
        onCopyDays={handleCopyDays}
        onAddDateForCopy={handleAddDateForCopy}
        onSwapFood={handleSwapFood}
        onUpdateMealInfo={updateMealInfo}
        clientPreferences={clientPreferences}
        clientId={clientIdFromUrl}
        cuisineTypes={getPreferencesForDay(activeDay).cuisineTypes}
        dayWisePreferences={dayWisePreferences}
        onSetDayPreferences={handleSetDayPreferences}
        onResetDayPreferences={handleResetToDefaults}
        getPreferencesForDay={getPreferencesForDay}
        hasCustomPreferences={hasCustomPreferences}
        editingDay={editingDay}
        onEditDay={setEditingDay}
        calculateDailyNutrients={calculateDailyNutrients}
        dayWiseNutrition={(draftData as any)?.nutrition?.dayWise || (draftData as any)?.dayWiseNutrition}
        clientData={selectedClient}
        draftId={draftId}
        onAddMealType={handleAddMealType}
        onDeleteMealSlot={handleDeleteMealSlot}
        onDraftDataUpdate={handleDraftDataUpdate}
        isDeletingAnyMealSlot={isDeletingAnyMealSlot}
        drvPrimaryTypes={drvPrimaryTypes}
        dayNames={dayNames}
        onDayNameUpdate={draftId ? async (day, dayName) => {
          const res = await updateMealPlanDayName(draftId, { day, dayName });
          if (res?.success) {
            setDayNames((prev) => ({ ...prev, [day]: dayName }));
            showSuccessToast(res?.message ?? 'Day name updated');
          } else {
            showErrorToast(res?.message ?? 'Failed to update day name');
          }
        } : undefined}
      />

      {allMealSlotsFilled && (isCheckingHardBlocks || finalizeValidation) && (
        <div className={`rounded-xl border p-4 ${
          finalizeValidation?.status === 'blocked'
            ? 'border-red-200 bg-red-50'
            : (finalizeValidation?.warnings?.length || 0) > 0
              ? 'border-amber-300 bg-amber-50'
            : 'border-emerald-200 bg-emerald-50'
        }`}>
          <div className="flex items-start gap-3">
            {isCheckingHardBlocks || !finalizeValidation ? (
              <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-slate-500" />
            ) : finalizeValidation?.status === 'blocked' ? (
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            ) : (finalizeValidation?.warnings?.length || 0) > 0 ? (
              <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
            ) : (
              <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-700" />
            )}
            <div className="min-w-0">
              <p className={`font-semibold ${
                finalizeValidation?.status === 'blocked'
                  ? 'text-red-900'
                  : (finalizeValidation?.warnings?.length || 0) > 0
                    ? 'text-amber-900'
                    : 'text-emerald-900'
              }`}>
                {isCheckingHardBlocks || !finalizeValidation
                  ? 'Checking clinical hard blocks…'
                  : finalizeValidation?.status === 'blocked'
                    ? `${countClinicalIssueGroups(finalizeValidation.blockingIssues)} clinical hard-block group${countClinicalIssueGroups(finalizeValidation.blockingIssues) === 1 ? '' : 's'} must be resolved`
                    : (finalizeValidation?.warnings?.length || 0) > 0
                      ? `${countClinicalIssueGroups(finalizeValidation.warnings)} clinical warning group${countClinicalIssueGroups(finalizeValidation.warnings) === 1 ? '' : 's'} to review before finalizing`
                    : 'Clinical hard-block checks passed'}
              </p>
              {finalizeValidation?.blockingIssues?.length ? (
                <ClinicalWarningsSummary
                  issues={finalizeValidation.blockingIssues}
                  tone="blocked"
                  className="mt-3 max-h-96 overflow-y-auto pr-1"
                />
              ) : null}
              {finalizeValidation?.warnings?.length ? (
                <ClinicalWarningsSummary
                  issues={finalizeValidation.warnings}
                  className="mt-3 max-h-96 overflow-y-auto pr-1"
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button 
          onClick={handleFinalizeMealPlan} 
          disabled={isFinalizing || isCheckingHardBlocks || draftData?.status !== 'draft'}
          className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
        >
          {isFinalizing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Finalizing...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4" />
              Finalize Plan
            </>
          )}
        </Button>
      </div>

      <DietReportModal
        open={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
        meals={meals}
        nutrientsByDay={getNutrientsByDay()}
        planDuration={effectivePlanDuration}
        nutrientGuideline={selectedClient?.drvRequirements?.source || selectedClient?.drvSource || "US IOM (Institute of Medicine)"}
        mealTypes={getAllMealTypes().map(m => m.uniqueId || m.mealType)}
        planAverageNutrition={(draftData as any)?.nutrition?.planLevel || (draftData as any)?.planLevelNutrition}
        clientProfile={selectedClient ? {
          sex: (selectedClient.gender as 'female' | 'male') || 'female',
          age: selectedClient.dateOfBirth 
            ? new Date().getFullYear() - new Date(selectedClient.dateOfBirth).getFullYear()
            : 35,
          height: selectedClient.heightCm || 165,
          weight: selectedClient.weightKg || 65,
          activityLevel: selectedClient.activityLevel || 'moderately_active',
          goals: selectedClient.healthGoals?.join(', ') || selectedClient.notes || 'Weight management and improved nutrition',
          // Add macro and micro targets from client
          ...(selectedClient as any)
        } : undefined}
      />

      <MealSelectionValidationModal
        open={validationModalOpen}
        onOpenChange={setValidationModalOpen}
        missingSelections={validateMealPlan()}
        onCancel={handleValidationCancel}
      />
      <ManualPlanConfirmDialog
        open={isFinalizeConfirmOpen}
        onOpenChange={(open) => {
          setIsFinalizeConfirmOpen(open);
          if (!open) setFinalizeDateConflicts([]);
        }}
        title={finalizeDateConflicts.length > 0 ? 'Overwrite existing meal plans?' : 'Finalize meal plan?'}
        description={
          finalizeDateConflicts.length > 0
            ? `The selected dates clash with ${finalizeDateConflicts.length} active meal plan(s). Continuing will mark these plans inactive: ${finalizeDateConflicts.map((item) => `${item.name} (${formatConflictDateRange(item)})`).join('; ')}.`
            : (
              <div className="space-y-4">
                {(finalizeValidation?.warnings?.length || 0) > 0 ? (
                  <>
                    <p>
                      Review {countClinicalIssueGroups(finalizeValidation?.warnings || [])} clinical warning group
                      {countClinicalIssueGroups(finalizeValidation?.warnings || []) === 1 ? '' : 's'} before finalizing.
                      Related items are combined by day and meal.
                    </p>
                    <ClinicalWarningsSummary issues={finalizeValidation?.warnings || []} />
                  </>
                ) : null}
                <p>
                  Are you sure you want to finalize this meal plan? This will convert the draft into a completed meal plan for{' '}
                  {selectedClient?.firstName || ''} {selectedClient?.lastName || ''}.
                </p>
              </div>
            )
        }
        confirmText={finalizeDateConflicts.length > 0 ? 'Overwrite and finalize' : 'Finalize'}
        loading={isFinalizing}
        onConfirm={() => proceedWithFinalize(finalizeDateConflicts.length > 0)}
      />
    </div>
      </Paper>
    </Box>
  );
};

export default ManualMealPlanDraftPage;
